import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CaretDown, Translate, House, Lightning, DownloadSimple, Question, BookOpen, ShieldCheck } from "@phosphor-icons/react";
import { useNavigation, SPALink } from "../context/NavigationContext";
import { useScrolled } from "../hooks/useScrolled";

const Navbar = () => {
  const isScrolled = useScrolled(20);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const { activeSection, changeLanguage: changeAppLanguage } = useNavigation();

  const navLinks = [
    { name: t("nav.home"), href: "/", section: "home" },
    { name: t("nav.features"), href: "/features", section: "features" },
    { name: t("nav.security"), href: "/securite", section: "securite" },
    { name: t("nav.download"), href: "/download", section: "download" },
    { name: t("nav.faq"), href: "/faq", section: "faq" },
    { name: t("nav.blog"), href: "/blog", section: "blog" },
    { name: t("nav.about"), href: "/about", section: "about" },
  ];

  const mobileNavLinks = [
    { name: t("nav.home"), href: "/", section: "home", icon: House },
    { name: t("nav.features"), href: "/features", section: "features", icon: Lightning },
    { name: t("nav.security"), href: "/securite", section: "securite", icon: ShieldCheck },
    { name: t("nav.download"), href: "/download", section: "download", icon: DownloadSimple },
    { name: t("nav.faq"), href: "/faq", section: "faq", icon: Question },
    { name: t("nav.blog"), href: "/blog", section: "blog", icon: BookOpen },
  ];

  const languages = [
    { code: "fr", name: "FR" },
    { code: "en", name: "EN" },
    { code: "es", name: "ES" },
    { code: "de", name: "DE" },
    { code: "it", name: "IT" },
    { code: "ru", name: "RU" },
    { code: "zh", name: "ZH" },
    { code: "pt", name: "PT" },
    { code: "ar", name: "AR" },
    { code: "ja", name: "JA" },
  ];

  const fullLanguages = [
    { code: "fr", name: "Français" },
    { code: "en", name: "English" },
    { code: "es", name: "Español" },
    { code: "de", name: "Deutsch" },
    { code: "it", name: "Italiano" },
    { code: "ru", name: "Русский" },
    { code: "zh", name: "中文" },
    { code: "pt", name: "Português" },
    { code: "ar", name: "العربية" },
    { code: "ja", name: "日本語" },
  ];

  const changeLanguage = (code) => {
    changeAppLanguage(code);
    setIsLangMenuOpen(false);
  };

  return (
    <>
      {/* ===== DESKTOP NAV — top bar ===== */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 pt-4 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center justify-between px-4 md:px-6 py-3 rounded-2xl transition-all duration-500 ${
            isScrolled 
              ? "bg-lumina-violet/20 backdrop-blur-xl border border-white/10 shadow-2xl shadow-lumina-violet/20" 
              : "bg-lumina-violet/10 backdrop-blur-lg border border-white/5"
          }`}>
            <SPALink to="/" className="flex items-center gap-4 group" data-testid="nav-logo">
              <div className="relative">
                <div className="w-16 h-16 rounded-xl overflow-hidden transform group-hover:scale-110 transition-transform duration-300">
                  <img
                    src="/images/lumina-logo.png"
                    alt="Lumina"
                    className="w-full h-full object-cover"
                    width="64"
                    height="64"
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                  />
                </div>
              </div>
              <span className="text-5xl font-bold text-white hidden sm:block">Lumina</span>
            </SPALink>

            <div className="flex items-center gap-4 lg:gap-6">
              {navLinks.map((link) => (
                <SPALink
                  key={link.name}
                  to={link.href}
                  className={`text-xs lg:text-sm font-medium transition-colors duration-300 ${
                    activeSection === link.section && !link.href.includes('#') 
                      ? 'text-lumina-violet' 
                      : 'text-white/80 hover:text-white'
                  }`}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </SPALink>
              ))}
              
              <div className="relative">
                <button
                  onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                  className="flex items-center gap-1 text-white/80 hover:text-white text-xs lg:text-sm font-medium transition-colors"
                >
                  <Translate size={16} weight="regular" />
                  <span className="hidden lg:inline">{languages.find(l => l.code === i18n.language)?.name || "FR"}</span>
                  <CaretDown size={12} weight="bold" className={`transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isLangMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-lumina-dark/95 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden shadow-xl min-w-[140px] z-50">
                    {fullLanguages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-lumina-violet/20 transition-colors ${
                          i18n.language === lang.code ? 'text-lumina-violet bg-lumina-violet/10' : 'text-white/80'
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* ===== MOBILE NAV — slim top bar + fixed bottom nav ===== */}

      {/* Top bar — logo + language only */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-3 pt-3 md:hidden">
        <div className={`flex items-center justify-between px-4 py-2.5 rounded-2xl transition-all duration-500 ${
          isScrolled 
            ? "bg-lumina-violet/20 backdrop-blur-xl border border-white/10 shadow-lg shadow-lumina-violet/15" 
            : "bg-lumina-violet/10 backdrop-blur-lg border border-white/5"
        }`}>
          <SPALink to="/" className="flex items-center gap-2.5" data-testid="nav-logo-mobile">
            <div className="w-10 h-10 rounded-lg overflow-hidden">
              <img
                src="/images/lumina-logo.png"
                alt="Lumina"
                className="w-full h-full object-cover"
                width="40"
                height="40"
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            </div>
            <span className="text-2xl font-bold text-white">Lumina</span>
          </SPALink>

          <div className="relative">
            <button
              onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
              className="flex items-center gap-1.5 text-white/80 hover:text-white px-3 py-1.5 rounded-lg bg-white/5 border border-white/10"
            >
              <Translate size={16} weight="regular" />
              <span className="text-xs font-medium">{languages.find(l => l.code === i18n.language)?.name || "FR"}</span>
              <CaretDown size={10} weight="bold" className={`transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {isLangMenuOpen && (
              <div className="absolute top-full right-0 mt-2 bg-lumina-dark/95 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden shadow-xl z-50">
                <div className="grid grid-cols-2 gap-0.5 p-1.5 min-w-[200px]">
                  {fullLanguages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`px-3 py-2 text-left text-sm rounded-lg hover:bg-lumina-violet/20 transition-colors ${
                        i18n.language === lang.code ? 'text-lumina-violet bg-lumina-violet/10' : 'text-white/80'
                      }`}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Bottom nav bar — fixed, always visible on mobile */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden">
        <div className="mx-3 mb-3 px-2 py-2 rounded-2xl bg-[#0d0d15]/90 backdrop-blur-xl border border-white/[0.08] shadow-2xl shadow-black/50"
          style={{ boxShadow: '0 -4px 30px rgba(0,0,0,0.5), 0 0 20px rgba(139,92,246,0.08)' }}>
          <div className="flex items-center justify-around">
            {mobileNavLinks.map((link) => {
              const isActive = activeSection === link.section;
              return (
                <SPALink
                  key={link.name}
                  to={link.href}
                  className={`flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl transition-all duration-200 ${
                    isActive ? 'text-lumina-violet' : 'text-gray-500'
                  }`}
                  data-testid={`mobile-nav-${link.section}`}
                >
                  <link.icon size={20} weight={isActive ? "duotone" : "regular"} 
                    className={isActive ? 'text-lumina-violet' : 'text-gray-500'}
                    style={isActive ? { filter: 'drop-shadow(0 0 6px rgba(139,92,246,0.6))' } : {}} />
                  <span className="text-[10px] font-medium">{link.name}</span>
                </SPALink>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
